import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, KeyRound, Loader2 } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

export default function AdminSecurity() {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStatus = async () => {
    const { data } = await api.get("/auth/2fa/status");
    setStatus(data);
  };

  useEffect(() => { loadStatus(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to start 2FA setup"));
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (!code) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      await api.post("/auth/2fa/verify", { code });
      toast.success("Two-factor authentication enabled");
      setSetup(null);
      setCode("");
      loadStatus();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Invalid code"));
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!code) return toast.error("Enter the 6-digit code from your authenticator to disable");
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code });
      toast.success("Two-factor authentication disabled");
      setCode("");
      loadStatus();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Invalid code"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader
        eyebrow="Super Admin"
        title="Security"
        description="Two-factor authentication for the Super Admin account."
        testid="admin-security-header"
      />
      <div className="px-4 sm:px-6 md:px-10 py-8 max-w-3xl space-y-6">
        <div className="border border-border p-6 space-y-4" data-testid="admin-2fa-panel">
          <div className="flex items-center gap-2">
            {status?.enabled ? <ShieldCheck className="w-5 h-5 text-emerald-500" /> : <ShieldOff className="w-5 h-5 text-muted-foreground" />}
            <div>
              <div className="font-semibold">Two-factor authentication (TOTP)</div>
              <div className="text-xs text-muted-foreground">
                {status?.enabled ? "Enabled — your account is protected." : "Not enabled — recommended for admin accounts."}
              </div>
            </div>
          </div>

          {!status?.enabled && !setup && (
            <Button onClick={startSetup} disabled={busy} className="rounded-none" data-testid="admin-2fa-start-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Set up 2FA
            </Button>
          )}

          {setup && (
            <div className="space-y-4 pt-2 border-t border-border" data-testid="admin-2fa-setup-panel">
              <p className="text-sm">Scan this QR code with Google Authenticator, 1Password, Authy, or any TOTP app:</p>
              <img src={setup.qr_data_url} alt="TOTP QR" className="border border-border p-2 bg-white" style={{ width: 220, height: 220 }} data-testid="admin-2fa-qr" />
              <div className="text-xs text-muted-foreground">
                Or enter this secret manually: <code className="font-mono select-all">{setup.secret}</code>
              </div>
              <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
                <div>
                  <Label className="label-eyebrow mb-2 inline-block">6-digit code from app</Label>
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    className="h-10 font-mono"
                    data-testid="admin-2fa-code-input"
                    inputMode="numeric"
                    autoFocus
                  />
                </div>
                <Button onClick={verify} disabled={busy || code.length < 6} className="rounded-none h-10" data-testid="admin-2fa-verify-btn">
                  {busy ? "Verifying…" : "Verify & enable"}
                </Button>
              </div>
            </div>
          )}

          {status?.enabled && (
            <div className="space-y-3 pt-2 border-t border-border">
              <p className="text-sm text-muted-foreground">To disable 2FA, enter a current code from your authenticator app.</p>
              <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
                <div>
                  <Label className="label-eyebrow mb-2 inline-block">Current 6-digit code</Label>
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    className="h-10 font-mono"
                    data-testid="admin-2fa-disable-code-input"
                    inputMode="numeric"
                  />
                </div>
                <Button onClick={disable} variant="destructive" disabled={busy || code.length < 6} className="rounded-none h-10" data-testid="admin-2fa-disable-btn">
                  Disable 2FA
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
